/* eslint-disable react/require-default-props */
import React from 'react';
import PropTypes from 'prop-types';
import { Button, FlexColumn, FlexRow, Typography } from 'design-system';
import PreviewChoiceIndicator from './PreviewChoiceIndicator';
import { getOptionId, isSameId } from '../surveyPreviewUtils';

function parseLimit(value) {
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

/**
 * Multi / skills / sequence options with Normal-survey selection rules:
 * min/max blocking and exclusiveSelection.
 */
export default function MultiSelectOption({
  options,
  question = null,
  preview = false,
  value,
  onChange,
  interactive = false,
  showSequence = false,
  onMinError,
  onMaxError,
  onClearErrors,
}) {
  if (!options.length) {
    return null;
  }

  const selectedValues = Array.isArray(value) ? value.map((id) => String(id)) : [];
  const minOptions = parseLimit(question?.min_options);
  const maxOptions = parseLimit(question?.max_options);

  const getSequenceNumber = (optionId) => {
    const index = selectedValues.findIndex((id) => isSameId(id, optionId));
    return index === -1 ? null : index + 1;
  };

  const isExclusiveOption = (optionId) => {
    const option = options.find((opt, index) => isSameId(getOptionId(opt, index), optionId));
    return Boolean(option?.exclusiveSelection);
  };

  const toggleOption = (optionId) => {
    if (!interactive) return;

    const normalizedId = String(optionId);
    const isSelected = selectedValues.some((id) => isSameId(id, normalizedId));

    if (isExclusiveOption(normalizedId) && !isSelected) {
      onClearErrors?.();
      onChange?.([normalizedId]);
      return;
    }

    if (isSelected) {
      if (minOptions != null && selectedValues.length <= minOptions) {
        onMinError?.();
        return;
      }
      onClearErrors?.();
      onChange?.(selectedValues.filter((id) => !isSameId(id, normalizedId)));
      return;
    }

    if (maxOptions != null && selectedValues.length >= maxOptions) {
      onMaxError?.();
      return;
    }

    const withoutExclusive = selectedValues.filter((id) => !isExclusiveOption(id));
    onClearErrors?.();
    onChange?.([...withoutExclusive, normalizedId]);
  };

  const optionButton = (option, index) => {
    const optionId = getOptionId(option, index);
    const selected = selectedValues.some((id) => isSameId(id, optionId));
    const sequenceNumber = showSequence && selected ? getSequenceNumber(optionId) : null;

    return (
      <Button
        type="button"
        key={optionId}
        variant="link"
        className={[
          preview ? 'cc-preview-choice-option' : 'radio-option w-100',
          selected ? 'cc-preview-choice-option--selected' : '',
        ]
          .filter(Boolean)
          .join(' ')}
        style={preview ? undefined : { margin: '0.5rem 0' }}
        aria-pressed={selected}
        disabled={!interactive}
        onClick={() => toggleOption(optionId)}
      >
        <FlexRow inline align="start" gap="var(--ds-space-2)">
          {preview ? (
            <PreviewChoiceIndicator type="checkbox" selected={selected} />
          ) : (
            <i
              className={`${selected ? 'fas fa-check-square' : 'far fa-square'} fa-lg radio-option-icon`}
              aria-hidden
            />
          )}
          {sequenceNumber != null ? (
            <Typography
              component="span"
              variant="body1"
              className="cc-preview-sequence-index"
              aria-label={`sequence ${sequenceNumber}`}
            >
              ({sequenceNumber})
            </Typography>
          ) : null}
          <Typography component="span" variant="body1" className="radio-option-label">
            {option.text}
          </Typography>
        </FlexRow>
      </Button>
    );
  };

  if (preview) {
    return (
      <FlexRow
        wrap
        align="start"
        justify="start"
        style={{ columnGap: 'var(--ds-space-5)', rowGap: 'var(--ds-space-3)' }}
        className="cc-preview-choice-list"
        role="group"
      >
        {options.map(optionButton)}
      </FlexRow>
    );
  }

  return (
    <FlexColumn align="stretch" className="pt-1" role="group">
      {options.map(optionButton)}
    </FlexColumn>
  );
}

MultiSelectOption.propTypes = {
  options: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  question: PropTypes.shape({
    min_options: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    max_options: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }),
  preview: PropTypes.bool,
  value: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  onChange: PropTypes.func,
  interactive: PropTypes.bool,
  showSequence: PropTypes.bool,
  onMinError: PropTypes.func,
  onMaxError: PropTypes.func,
  onClearErrors: PropTypes.func,
};
